import { Form, Input, Button } from 'antd';
import { useState } from 'react';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
// 导入图标
import { PlusCircleOutlined } from '@ant-design/icons';

/**
 * Android SHA256 证书添加表单组件
 */
export const SHA256Form = ({
  onSubmit,
}: {
  onSubmit: (sha256Hash: string, description: string) => Promise<void>;
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 处理表单提交
   */
  const onFinish = async (values: { sha256_hash: string; description: string }): Promise<void> => {
    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(values.sha256_hash, values.description);
      form.resetFields();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '证书添加失败，请检查指纹是否正确。';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
        <PlusCircleOutlined className="text-blue-500" />
        添加 Android SHA256 证书
      </h3>
      
      {error && (
        <ErrorAlert 
          message="操作失败" 
          description={error} 
          className="mb-6"
          showIcon
        />
      )}

      <Form
        form={form}
        name="sha256"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className="space-y-6"
      >
        <Form.Item
          name="sha256_hash"
          label="SHA256 证书指纹"
          tooltip="请输入64位十六进制的证书指纹，勿包含冒号，可从Android Studio或签名工具获取。"
          rules={[
            { required: true, message: '请输入 SHA256 证书指纹' },
            { 
              pattern: /^[a-fA-F0-9]{64}$/,
              message: 'SHA256 必须是 64 位十六进制字符串（无冒号）'
            }
          ]}
        >
          <Input.TextArea
            placeholder="例如：AA11BB22CC33DD44EE55FF667788990011223344556677889900AABBCCDDEEFF"
            rows={3}
            size="large"
            disabled={submitting}
            autoSize={{ minRows: 3, maxRows: 4 }}
            className="font-mono"
            style={{ borderRadius: '8px' } as React.CSSProperties}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="证书描述"
          tooltip="添加描述便于区分不同环境的证书（如生产/测试）"
          rules={[{ required: true, message: '请输入证书描述' }]}
        >
          <Input 
            placeholder="例如：生产环境-正式签名证书、测试环境-调试证书" 
            size="large"
            disabled={submitting}
            style={{ borderRadius: '8px' } as React.CSSProperties}
          />
        </Form.Item>

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            disabled={submitting}
            icon={<PlusCircleOutlined />}
            className="w-full py-6 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transition-all"
          >
            {submitting ? '添加中...' : '确认添加证书'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};